/**
 * A CALLED TEST'S ANSWER ON THE WIRE (v9.5.3) - what the asked screen
 * sends back when it rolled the test itself: the result, named for the
 * call and the person, so the GM's screen can tell it is the one asked
 * for before it writes the line.
 *
 * The answer to a SECRET prompt (or from a build that could not roll) is
 * the plain signal `true`, and is not read here.
 *
 * Pure, zero React dependency.
 */
import type { CalledResult } from './callForTest';

export interface TestAnswer {
  callId: string;
  combatantId: string;
  result: CalledResult;
}

const text = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

function readResult(raw: unknown, combatantId: string): CalledResult | null {
  const r = (raw ?? null) as Partial<CalledResult> | null;
  const summary = text((r?.entry as { summary?: unknown } | undefined)?.summary);
  if (!r || !summary || typeof r.margin !== 'number' || !Number.isFinite(r.margin) || r.margin < 0) return null;
  if (r.passed !== true && r.passed !== false && r.passed !== null) return null;
  return {
    combatantId,
    name: text(r.name) ?? 'Someone',
    entry: { summary, detail: (r.entry as { detail?: unknown }).detail ?? {} } as CalledResult['entry'],
    passed: r.passed,
    margin: r.margin,
  };
}

/** An answer that crossed the wire, validated; null for the signal, or anything malformed. */
export function readTestAnswer(raw: unknown): TestAnswer | null {
  const stored = (raw ?? null) as Record<string, unknown> | null;
  const callId = text(stored?.callId);
  const combatantId = text(stored?.combatantId);
  if (!stored || !callId || !combatantId) return null;
  const result = readResult(stored.result, combatantId);
  return result ? { callId, combatantId, result } : null;
}

/** Is this the answer to that prompt? The same call and the same person, nothing else counts. */
export function answerMatches(prompt: { callId: string; combatantId: string }, answer: TestAnswer): boolean {
  return prompt.callId === answer.callId && prompt.combatantId === answer.combatantId;
}

/** The answer the asked screen sends: its roll of the prompt's call. */
export function testAnswerOf(prompt: { callId: string; combatantId: string }, result: CalledResult): TestAnswer {
  return { callId: prompt.callId, combatantId: prompt.combatantId, result };
}
